"use client";

import { useCallback, useSyncExternalStore } from "react";
import AccountFlyoutShell from "@/components/layout/AccountFlyoutShell";
import AppThemeMenu from "@/components/layout/AppThemeMenu";
import {
  BugBeetle,
  Download,
  Eye,
  LogOut,
  Skull,
} from "@/components/ui/icons";
import {
  canTriggerSentryTestCrash,
  triggerSentryTestCrash,
} from "@/lib/sentry";
import type { AuthUser } from "@/store/slices/auth.slice";

const SESSION_MENU_BREAKPOINT = "(min-width: 40rem)";
const actionButtonClass =
  "flex w-full items-center gap-2 rounded-[0.9rem] px-3 py-2.5 text-left text-sm font-semibold text-foreground transition hover:bg-[color:var(--button-ghost-hover)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/20";
const destructiveActionClass =
  "flex w-full items-center gap-2 rounded-[0.9rem] px-3 py-2.5 text-left text-sm font-[650] text-destructive transition hover:bg-destructive/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/20";
const sessionAlertBadgeClass =
  "inline-flex min-w-4 items-center justify-center rounded-full border border-destructive/25 bg-destructive px-[0.35rem] text-[0.625rem] font-extrabold leading-none text-destructive-foreground";

type SessionMenuActionHandler = () => void | Promise<void>;

function useMediaQueryMatch(query: string, enabled = true) {
  const subscribe = useCallback(
    (onStoreChange: () => void) => {
      if (!enabled || typeof window === "undefined") {
        return () => {};
      }

      const mediaQuery = window.matchMedia(query);

      if (typeof mediaQuery.addEventListener === "function") {
        mediaQuery.addEventListener("change", onStoreChange);
        return () => {
          mediaQuery.removeEventListener("change", onStoreChange);
        };
      }

      mediaQuery.addListener(onStoreChange);
      return () => {
        mediaQuery.removeListener(onStoreChange);
      };
    },
    [enabled, query],
  );

  const getSnapshot = useCallback(() => {
    if (!enabled || typeof window === "undefined") {
      return false;
    }

    return window.matchMedia(query).matches;
  }, [enabled, query]);

  return useSyncExternalStore(subscribe, getSnapshot, () => false);
}

type UserAccountMenuProps = {
  user: AuthUser;
  variant?: "default" | "session";
  compactTrigger?: boolean;
  showThemeToggle?: boolean;
  showUserId?: boolean;
  onLogout: () => Promise<void>;
  onExportAllData?: SessionMenuActionHandler;
  onOpenSessionLog?: SessionMenuActionHandler;
  onViewAllData?: SessionMenuActionHandler;
  sessionLogAlertCount?: number;
};

export default function UserAccountMenu({
  user,
  variant = "default",
  compactTrigger = false,
  showThemeToggle = false,
  showUserId = true,
  onLogout,
  onExportAllData,
  onOpenSessionLog,
  onViewAllData,
  sessionLogAlertCount = 0,
}: UserAccountMenuProps) {
  const userProvider = user.provider ?? null;
  const isSessionVariant = variant === "session";
  const showDesktopDataAction = useMediaQueryMatch(
    SESSION_MENU_BREAKPOINT,
    isSessionVariant,
  );
  const normalizedSessionLogAlertCount =
    typeof sessionLogAlertCount === "number" &&
    Number.isFinite(sessionLogAlertCount)
      ? Math.max(0, Math.trunc(sessionLogAlertCount))
      : 0;
  const hasExportAllDataAction = isSessionVariant && Boolean(onExportAllData);
  const hasOpenSessionLogAction =
    isSessionVariant && Boolean(onOpenSessionLog);
  const hasViewAllDataAction =
    isSessionVariant && showDesktopDataAction && Boolean(onViewAllData);
  const hasSessionDataActions =
    hasExportAllDataAction || hasViewAllDataAction;
  const hasSessionActions = hasOpenSessionLogAction || hasSessionDataActions;
  const showTestCrashAction = canTriggerSentryTestCrash();
  const providerIconLabel =
    userProvider === "spotify"
      ? "Spotify account"
      : userProvider === "youtube"
        ? "YouTube account"
        : userProvider === "applemusic"
          ? "Apple Music account"
          : userProvider === "slack"
            ? "Slack account"
            : "Account";

  const handleLogoutClick = async () => {
    await onLogout();
  };

  const runMenuAction = (action?: SessionMenuActionHandler) => {
    if (!action) return;
    void Promise.resolve().then(() => action());
  };

  const handleTestCrashClick = () => {
    if (
      typeof window !== "undefined" &&
      !window.confirm(
        "Crash this app now to test Sentry crash reporting? This will immediately close the app."
      )
    ) {
      return false;
    }

    triggerSentryTestCrash("user_account_menu");
    return true;
  };

  return (
    <AccountFlyoutShell
      user={user}
      variant={isSessionVariant ? "session" : "default"}
      headerEyebrow={providerIconLabel}
      triggerAriaLabel={`Open account settings (${providerIconLabel})`}
      triggerTitle="Account settings"
      triggerIconOnly={compactTrigger}
      showUserId={showUserId}
    >
      {({ close }) => (
        <>
          {hasSessionActions ? (
            <>
              <div className="theme-menu-separator h-px" />
              <div className="grid gap-1 px-2 py-1.5">
                {hasOpenSessionLogAction ? (
                  <button
                    type="button"
                    onClick={() => {
                      close();
                      runMenuAction(onOpenSessionLog);
                    }}
                    className={actionButtonClass}
                    aria-label={
                      normalizedSessionLogAlertCount > 0
                        ? `Open session log with ${normalizedSessionLogAlertCount} failures`
                        : "Open session log"
                    }
                  >
                    <BugBeetle className="size-4 shrink-0 text-muted-foreground" />
                    <span className="min-w-0 flex-1">Session log</span>
                    {normalizedSessionLogAlertCount > 0 ? (
                      <span className={sessionAlertBadgeClass}>
                        {normalizedSessionLogAlertCount}
                      </span>
                    ) : null}
                  </button>
                ) : null}
                {hasViewAllDataAction ? (
                  <button
                    type="button"
                    onClick={() => {
                      close();
                      runMenuAction(onViewAllData);
                    }}
                    className={actionButtonClass}
                  >
                    <Eye className="size-4 shrink-0 text-muted-foreground" />
                    View all data
                  </button>
                ) : null}
                {hasExportAllDataAction ? (
                  <button
                    type="button"
                    onClick={() => {
                      close();
                      runMenuAction(onExportAllData);
                    }}
                    className={actionButtonClass}
                  >
                    <Download className="size-4 shrink-0 text-muted-foreground" />
                    Export all data
                  </button>
                ) : null}
              </div>
            </>
          ) : null}

          {showThemeToggle ? (
            <>
              <div className="theme-menu-separator h-px" />
              <div className="px-2 py-1.5">
                <AppThemeMenu
                  showLabel
                  className="w-full justify-between rounded-[0.9rem] px-3 py-2.5"
                />
              </div>
            </>
          ) : null}

          <div className="theme-menu-separator h-px" />
          <div className="grid gap-1 p-2 pt-1.5">
            {showTestCrashAction ? (
              <button
                type="button"
                onClick={() => {
                  if (!handleTestCrashClick()) return;
                  close();
                }}
                className={destructiveActionClass}
              >
                <Skull className="size-4 shrink-0" />
                Test crash
              </button>
            ) : null}
            <button
              type="button"
              onClick={() => {
                close();
                void handleLogoutClick();
              }}
              className={destructiveActionClass}
            >
              <LogOut className="size-4 shrink-0" />
              Logout
            </button>
          </div>
        </>
      )}
    </AccountFlyoutShell>
  );
}
